import { Outlet } from 'react-router-dom';
import {
  AppBarSurface,
  Badge,
  Box,
  Button,
  Caption,
  Container,
  Heading,
  Inline,
  Nav,
  PageFooter,
  PageMain,
  SkipLink,
  Stack,
  Text,
  Toolbar,
  gradient,
  layout
} from '@srichaitra/ui';
import { labels } from '@srichaitra/constants';
import { selectCurrentUser, signOut, useAppDispatch, useAppSelector } from '@srichaitra/store';
import { AppNavLink } from './AppNavLink';
import { NotificationCenter } from './NotificationCenter';

/**
 * The application container.
 *
 * Every signed-in page renders through the <Outlet /> here, so the top bar,
 * navigation, notification area and footer are mounted once for the whole
 * session rather than rebuilt per screen. A page therefore only ever describes
 * its own content — it never draws chrome.
 */
export function AppShell({ navItems = [] }) {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);

  // flex:1 rather than minHeight:100% — #root is a flex column that grows with
  // the page, so a percentage height here resolves against an auto height and
  // collapses. Filling the flex parent works at any page length.
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* First in the tab order, off-screen until focused. Without one, a
          keyboard user tabs through the whole navigation on every page. */}
      <SkipLink targetId="main">{labels.nav.skipToContent}</SkipLink>

      <AppBarSurface>
        <Container>
          <Toolbar sx={{ gap: 4, justifyContent: 'space-between' }}>
            <Inline gap={4}>
              <Heading level={1} size="h3" sx={{ color: 'primary.main', whiteSpace: 'nowrap' }}>
                {labels.app.name}
              </Heading>

              <Nav aria-label={labels.nav.menu}>
                <Inline gap={1}>
                  {navItems.map((item) => (
                    <AppNavLink key={item.to} to={item.to}>
                      {item.label}
                    </AppNavLink>
                  ))}
                </Inline>
              </Nav>
            </Inline>

            <Inline gap={3}>
              {user ? (
                // Hidden on narrow screens: the sign-out button is what has to
                // survive, and the name is repeated on the dashboard anyway.
                <Inline gap={2} sx={{ display: { xs: 'none', sm: 'flex' }, whiteSpace: 'nowrap' }}>
                  <Text as="span" size="caption" weight="medium">
                    {user.displayName || user.userId}
                  </Text>
                  <Badge tone="accent">{user.roleLabel || user.role}</Badge>
                </Inline>
              ) : null}

              <Button variant="secondary" size="sm" onClick={() => dispatch(signOut())}>
                {labels.nav.signOut}
              </Button>
            </Inline>
          </Toolbar>
        </Container>
      </AppBarSurface>

      <PageMain id="main" sx={{ flex: 1, py: 8 }}>
        <Container>
          <Stack gap={6}>
            <NotificationCenter />
            {/* Every page in the application renders here. */}
            <Outlet />
          </Stack>
        </Container>
      </PageMain>

      <PageFooter sx={{ borderTop: 1, borderColor: 'divider', py: 4 }}>
        <Container>
          <Caption>
            {labels.app.name} · {labels.app.tagline}
          </Caption>
        </Container>
      </PageFooter>
    </Box>
  );
}

/**
 * The container for pages shown to a signed-out visitor.
 *
 * Deliberately separate from AppShell: a login screen carrying a navigation bar
 * full of links the visitor cannot follow is worse than one without. This is
 * also the only place the brand gradient appears — the sign-in screen is where
 * the product gets to introduce itself.
 */
export function AuthShell() {
  return (
    <PageMain
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Fills the flex column that #root is, so the brand gradient covers the
        // whole viewport on a short page and still grows on a tall one.
        flex: 1,
        p: 6,
        background: gradient
      }}
    >
      <Stack gap={6} sx={{ width: '100%', maxWidth: layout.authCardMaxWidth }}>
        <Stack gap={1} align="center">
          <Heading level={1} size="h1" sx={{ color: 'common.white' }}>
            {labels.app.name}
          </Heading>
          <Caption sx={{ color: 'common.white', opacity: 0.85 }}>{labels.app.tagline}</Caption>
        </Stack>

        <NotificationCenter />
        <Outlet />
      </Stack>
    </PageMain>
  );
}
